import { AxiosError, AxiosResponse, RawAxiosRequestHeaders } from 'axios';

import { ProductType } from '@tink/core/src/types';
import { uuid } from '../../utilities/utils';

export const createApiURL = (path: string): string => {
  return `/api-proxy${path}`;
};

export const createApiResponse = <T>(
  data: T,
  status: number,
  headers?: AxiosResponse['headers']
): ResponseObj<T> => {
  return {
    data,
    headers,
    isOk(): boolean {
      return status === 200 || status === 204;
    },
    status,
  };
};

export const apiResponse = <T>(axiosResponse: AxiosResponse<T>): ResponseObj<T> => {
  const { data, status, headers } = axiosResponse;

  const response: ResponseObj<T> = createApiResponse<T>(data, status, headers);

  return response;
};

export const createApiErrorResponse = <FailureResponseType>(
  axiosError: AxiosError<FailureResponseType>
): ResponseObj<FailureResponseType> => {
  const response = {
    data: axiosError.response ? axiosError.response.data : undefined,
    status: axiosError.response ? axiosError.response.status : 0,
    isOk: () => false,
  };

  return response as ResponseObj<FailureResponseType>;
};

export const wrap = (method: () => void) => {
  try {
    method();
  } catch (err) {
    window.console.log(err);
  }
};

export type ResponseObj<T> = {
  data: T;
  isOk: () => boolean;
  status: number;
  headers?: AxiosResponse['headers'];
};

type Headers = RawAxiosRequestHeaders;

export const defaultHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

export class Base {
  authorizationToken?: string;

  version?: string;

  clientName?: string;

  clientId?: string;

  sessionId?: string;

  product?: ProductType;

  setAuthorization(token: string) {
    this.authorizationToken = token;
  }

  unsetAuthorization() {
    this.authorizationToken = undefined;
  }

  setVersion(version: string) {
    this.version = version;
  }

  setClientName(clientName: string) {
    this.clientName = clientName;
  }

  setProduct(product: ProductType) {
    this.product = product;
  }

  setClientId(clientId: string) {
    this.clientId = clientId;
  }

  getClientId() {
    return this.clientId;
  }

  setSessionId(id: string) {
    this.sessionId = id;
  }

  getAnalyticsHeaders() {
    return {
      'X-Tink-SDK-Name': 'Tink Link Web',
      'X-Tink-SDK-Product': this.product,
      'X-Tink-SDK-Version': this.version,
    };
  }

  headers(): Headers {
    const headers: Headers = {
      ...defaultHeaders,
    };

    // This part only happens when request is coming from within link
    if (this.clientId) {
      headers['X-Tink-OAuth-Client-ID'] = this.clientId;
    }

    headers['X-Client-Trace-ID'] = `${this.sessionId}/${uuid()}`;

    Object.assign(headers, this.getAnalyticsHeaders());

    if (this.authorizationToken) {
      headers.Authorization = `Bearer ${this.authorizationToken}`;
    }

    return headers;
  }
}

export const isResponseOk = <S, F>(
  response: ResponseObj<S> | ResponseObj<F>
): response is ResponseObj<S> => {
  return response.isOk();
};

export const base = new Base();
